import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Navigate } from "react-router-dom";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { ModuleIcon } from "@/components/ModuleIcon";
import { ConstructionBadge } from "@/components/ConstructionBadge";
import { MODULES, IMAGES } from "@/data/constants";
import { MODULE_CONTENT } from "@/data/moduleContent";
import { MODULE_IMAGES, MODULE_TIPS } from "@/data/moduleMedia";
import { SEO } from "@/components/SEO";
import { localizePricing } from "@/data/pricingI18n";
import { PricePlanCard } from "@/components/PricePlanCard";
import { ModuleTitle } from "@/components/ProgereText";
import { ContractDialog } from "@/components/ContractDialog";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Check, Crown, ArrowLeft, ExternalLink, Sparkles, CheckCircle2, HardHat, Info, Lightbulb } from "lucide-react";

export default function ModuleDetailPage() {
  const { key } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const [contract, setContract] = useState(null);
  const [payrollSel, setPayrollSel] = useState({});

  const module = MODULES.find((m) => m.key === key);
  const content = MODULE_CONTENT[key];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [key]);

  if (!module || !content) return <Navigate to="/" replace />;

  const accent = module.color === "gold" ? "#EAB308" : "#dc143c";
  const pricingModule = localizePricing(lang).pricing.find((m) => m.key === key);

  const choose = (mod, plan, addonOn) => {
    if (!user) {
      toast.info(t("calc.loginFirst"));
      navigate("/login");
      return;
    }
    const addon = plan.payroll || plan.takeoff;
    const addonKind = plan.payroll ? "PAIE" : plan.takeoff ? "TAKEOFF" : null;
    const addonActive = addonOn && addon;
    const name = addonActive ? `${mod.name} ${plan.label} + ${addonKind} ${plan.label}` : `${mod.name} ${plan.label}`;
    setContract({
      module: { key: mod.key, name: mod.name },
      plan: {
        key: `${mod.key}-${plan.tier}${addonActive ? (plan.payroll ? "-paie" : "-takeoff") : ""}`,
        name,
        price: plan.price,
        period: "CAD/mois",
        tier: plan.tier,
      },
      payroll: addonActive ? `${addonKind} ${plan.label}` : null,
      addonKind: addonActive ? addonKind : null,
    });
  };

  const openModule = () => window.open(module.subdomain, "_blank", "noopener");

  return (
    <div className="min-h-screen bg-[#050505]" data-testid={`module-page-${key}`}>
      <SEO title={module.name} path={`/module/${key}`} description={t(`moduleData.${key}.description`)} />
      <Header />

      {/* Hero */}
      <section className="relative pt-28 pb-16 overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMAGES.hero} alt="" className="w-full h-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/85 via-black/80 to-[#050505]" />
          <div className="absolute inset-0 pg-grid-bg opacity-50" />
          <div
            className="absolute top-0 right-0 w-[500px] h-[500px]"
            style={{ background: `radial-gradient(circle, ${accent}1f 0%, transparent 65%)` }}
          />
        </div>

        <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
          <button
            onClick={() => navigate("/#modules")}
            className="inline-flex items-center gap-2 text-sm text-white/50 hover:text-white transition-colors mb-8"
            data-testid="back-to-modules"
          >
            <ArrowLeft className="w-4 h-4" /> {t("md.allModules")}
          </button>

          <div className="flex items-start gap-5 max-w-3xl">
            <div
              className="flex h-16 w-16 shrink-0 items-center justify-center rounded-lg border"
              style={{ borderColor: `${accent}55`, background: `${accent}14` }}
            >
              <ModuleIcon name={module.icon} className="w-8 h-8" style={{ color: accent }} />
            </div>
            <div>
              <div className="flex items-center gap-3 flex-wrap">
                <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight">
                  <ModuleTitle name={module.name} />
                </h1>
                {module.premium && (
                  <span
                    className="inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold uppercase"
                    style={{ color: accent, background: `${accent}18`, border: `1px solid ${accent}33` }}
                  >
                    <Crown className="w-3.5 h-3.5" /> {module.premium} Premium
                  </span>
                )}
                {module.construction && <ConstructionBadge />}
              </div>
              <p className="mt-3 text-xl text-white/70">{content.hero}</p>
            </div>
          </div>

          <p className="mt-6 max-w-3xl text-white/60 text-lg leading-relaxed">{content.intro}</p>

          {module.construction && (
            <div className="mt-6 max-w-3xl rounded-lg border border-[#f59e0b]/30 bg-[#f59e0b]/10 px-5 py-3.5 text-sm text-[#f5c77e] flex items-center gap-2.5" data-testid="construction-notice">
              <HardHat className="w-5 h-5 shrink-0" />
              Ce module est actuellement <b className="font-semibold">&nbsp;en construction&nbsp;</b> et sera disponible prochainement. Vous pouvez déjà réserver votre forfait.
            </div>
          )}

          <div className="mt-8 flex flex-col sm:flex-row gap-4">
            <Button
              onClick={() => document.getElementById("module-forfaits")?.scrollIntoView({ behavior: "smooth" })}
              data-testid="module-see-plans"
              className="h-12 px-7 bg-[#EAB308] text-black font-semibold rounded-md gap-2 hover:opacity-90 transition-opacity"
            >
              {t("md.seePlansPrices")}
            </Button>
            {user && (
              <Button
                onClick={openModule}
                variant="outline"
                data-testid="module-open"
                className="h-12 px-7 border-white/20 bg-white/5 text-white hover:border-[#EAB308]/60 hover:bg-white/10 rounded-md gap-2 transition-colors"
              >
                <ExternalLink className="w-5 h-5" /> {t("md.openModule")}
              </Button>
            )}
          </div>
        </div>
      </section>

      {MODULE_IMAGES[key] && (
        <section className="pb-2" data-testid="module-visual">
          <div className="mx-auto max-w-6xl px-5 lg:px-8">
            <div className="overflow-hidden rounded-2xl border border-white/10 shadow-2xl">
              <img src={MODULE_IMAGES[key]} alt={module.name} className="w-full object-cover" loading="lazy" />
            </div>
          </div>
        </section>
      )}

      {/* Features */}
      <section className="py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-extrabold text-white">
            {t("md.featuresA")} <span className="text-[#EAB308]">{t("md.featuresB")}</span>
          </h2>
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {content.features.map((f) => (
              <div key={f.title} className="rounded-lg border border-white/10 bg-[#0a0a0a] p-6" data-testid={`feature-${f.title}`}>
                <div className="flex h-10 w-10 items-center justify-center rounded-md" style={{ background: `${accent}14`, border: `1px solid ${accent}30` }}>
                  <CheckCircle2 className="w-5 h-5" style={{ color: accent }} />
                </div>
                <h3 className="mt-4 font-semibold text-white">{f.title}</h3>
                <p className="mt-1.5 text-sm text-white/55 leading-relaxed">{f.text}</p>
              </div>
            ))}
          </div>

          {/* Use cases */}
          <div className="mt-12 rounded-xl border border-white/10 bg-gradient-to-br from-white/[0.04] to-transparent p-8">
            <h3 className="text-xl font-bold text-white flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-[#EAB308]" /> {t("md.forWho")}
            </h3>
            <ul className="mt-5 grid gap-3 sm:grid-cols-3">
              {content.useCases.map((u) => (
                <li key={u} className="flex items-start gap-2.5 text-sm text-white/70">
                  <Check className="w-4 h-4 mt-0.5 shrink-0" style={{ color: accent }} />
                  {u}
                </li>
              ))}
            </ul>
          </div>

          {MODULE_TIPS[key] && (
            <div className="mt-8 rounded-xl border border-[#EAB308]/20 bg-[#EAB308]/[0.05] p-8" data-testid="module-tips">
              <h3 className="text-xl font-bold text-white flex items-center gap-2">
                <Lightbulb className="w-5 h-5 text-[#EAB308]" /> {lang === "en" ? "PRO tips" : "Astuces PRO"}
              </h3>
              <div className="mt-5 grid gap-4 sm:grid-cols-3">
                {(MODULE_TIPS[key][lang] || MODULE_TIPS[key].fr).map((tip, i) => (
                  <div key={i} className="rounded-lg border border-white/10 bg-black/30 p-4 text-sm text-white/70 leading-relaxed">
                    <span className="text-[#EAB308] font-mono text-xs">{String(i + 1).padStart(2, "0")}</span>
                    <p className="mt-2">{tip}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Forfaits */}
      <section id="module-forfaits" className="py-16 lg:py-24 border-t border-white/10">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="max-w-2xl">
            <span className="text-xs font-mono uppercase tracking-[0.3em]" style={{ color: accent }}>
              {t("md.pricing")}
            </span>
            <h2 className="mt-3 text-3xl sm:text-4xl font-extrabold text-white">
              {t("md.unlock")} {module.name}
            </h2>
            <p className="mt-3 text-white/60 text-lg">
              {t("md.plansDesc")}
            </p>
          </div>

          {pricingModule?.note && (
            <div className="mt-6 flex items-start gap-2 rounded-lg border border-[#EAB308]/25 bg-[#EAB308]/[0.06] px-4 py-3 text-sm text-[#f3d98a]">
              <Info className="w-4 h-4 mt-0.5 shrink-0 text-[#EAB308]" />
              {pricingModule.note}
            </div>
          )}

          <div className="mt-8 grid gap-6 md:grid-cols-3">
            {pricingModule?.plans.map((plan) => {
              const selKey = `${pricingModule.key}-${plan.tier}`;
              return (
                <PricePlanCard
                  key={plan.tier}
                  module={module}
                  plan={plan}
                  onChoose={choose}
                  payrollOn={payrollSel[selKey]}
                  togglePayroll={() => setPayrollSel((s) => ({ ...s, [selKey]: !s[selKey] }))}
                />
              );
            })}
          </div>

          <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-4">
            <Button
              onClick={() => navigate("/tarification")}
              variant="outline"
              data-testid="module-see-full-pricing"
              className="h-11 px-6 border-white/20 bg-white/5 text-white hover:border-[#EAB308]/60 hover:bg-white/10 rounded-md transition-colors"
            >
              {t("md.seeAllPricing")}
            </Button>
            <p className="text-xs text-white/35">
              {t("md.purchaseNote")}
            </p>
          </div>
        </div>
      </section>

      {contract && (
        <ContractDialog
          module={contract.module}
          plan={contract.plan}
          payroll={contract.payroll}
          addonKind={contract.addonKind}
          onClose={() => setContract(null)}
        />
      )}

      <Footer />
    </div>
  );
}
